import React, { useMemo } from 'react'
import { AutoComplete } from 'antd';
import { durationTileData, frequencyData, instructions, routesData } from '../../../../../utils/static-data/data';

// Take / Route / Frequency / Instruction / SIG / Duration rows. Shared by the
// medicine SIG form and the Generic SIG settings editor. Fully controlled:
// the parent owns `info` + `sig` and composes the SIG in onFieldChange.
// highlight: values were auto-filled from a saved SIG and still need a look.
const SigFields = ({ info, sig, language, highlight = false, onFieldChange, onSigChange, onDurationPick }) => {

    // Combined duration quick picks ("2 days", "১ দিন", ...): selecting one splits
    // into the count input + unit select. The field itself stays free-text.
    const toBanglaDigits = (value) => String(value).replace(/[0-9]/g, (d) => "০১২৩৪৫৬৭৮৯"[d])
    const durationQuickPicks = useMemo(() => {
        const bn = language == 'bangla'
        const picks = []
        const push = (count, unitEn, unitBn, labelEn) => {
            const num = bn ? toBanglaDigits(count) : String(count)
            picks.push({
                value: bn ? `${num} ${unitBn}` : labelEn, // shown in the dropdown
                count: num,                               // -> count input
                unit: bn ? unitBn : unitEn,               // -> unit select (exact option value)
            })
        };
        [1, 2, 3, 4, 5, 6, 7, 10, 14, 15, 20, 21, 28, 30, 40, 45].forEach((n) => push(n, "Day(s)", "দিন", `${n} ${n > 1 ? "days" : "day"}`));
        [1, 2, 3, 4].forEach((n) => push(n, "Week(s)", "সপ্তাহ", `${n} ${n > 1 ? "weeks" : "week"}`));
        [1, 2, 3, 6].forEach((n) => push(n, "Month(s)", "মাস", `${n} ${n > 1 ? "months" : "month"}`));
        picks.push({ value: bn ? "চলবে" : "Continue", count: bn ? "চলবে" : "continue", unit: bn ? "চলবে" : "Continue" })
        return picks
    }, [language])

    return (
        <div className={`col-12 m-0 p-0 sig-fields ${highlight ? "sig-fields--autofilled" : ""}`}>
            <div className="col-12 m-0 p-0 individual-input d-flex">
                <label htmlFor="" className="form-label mt-1">
                    Take:
                </label>
                <input className="form-control input w-50" autoComplete="off" type="text"
                    value={info?.take || ""}
                    placeholder="Take"
                    onChange={(e) => {
                        onFieldChange("take", e.target.value);
                    }}
                />

            </div>
            <div className="col-12 m-0 p-0 individual-input d-flex ">
                <label htmlFor="exampleInputEmail1" className="form-label mt-1">
                    Route:
                </label>
                <input className="w-50  form-control input" autoComplete="off" type="text"
                    value={info?.route || ""}
                    placeholder="Route"
                    onChange={(e) => {
                        onFieldChange("route", e.target.value);
                    }}
                />
                <select
                    onChange={(e) => {
                        onFieldChange("route", e.target.value);
                    }}
                >
                    <option value={""}>{language == 'bangla' ? "নির্বাচন করুন" : "Select"}</option>
                    {routesData?.map((data, index) => {
                        return <option key={index} value={language == 'bangla' ? data?.value_bn : data?.value_en}>{language == 'bangla' ? data?.value_bn : data?.value_en}</option>
                    })}
                </select>

            </div>
            <div className="col-12 m-0 p-0 individual-input d-flex">
                <label htmlFor="exampleInputEmail1" className="form-label mt-1">
                    Frequency:
                </label>
                <input className="w-50  form-control input" autoComplete="off" type="text"
                    value={info?.frequency || ""}
                    placeholder="Frequency"
                    onChange={(e) => {
                        onFieldChange("frequency", e.target.value);
                    }}
                />
                <select
                    onChange={(e) => {
                        onFieldChange("frequency", e.target.value);
                    }}
                >
                    <option value={""}>{language == 'bangla' ? "নির্বাচন করুন" : "Select"}</option>
                    {frequencyData?.map((data, index) => {
                        return <option key={index} value={language == 'bangla' ? data?.value_bn : data?.value_en}>{language == 'bangla' ? data?.value_bn : data?.value_en}</option>
                    })}
                </select>
            </div>

            <div className="col-12 m-0 p-0 individual-input d-flex">
                <label htmlFor="exampleInputEmail1" className="form-label mt-1">
                    Instruction:
                </label>
                <input className="w-50  form-control input" autoComplete="off" type="text"
                    value={info?.instruction || ""}
                    placeholder="instruction"
                    onChange={(e) => {
                        onFieldChange("instruction", e.target.value);
                    }}
                />
                <select
                    onChange={(e) => {
                        onFieldChange("instruction", e.target.value);
                    }}
                >
                    <option value={""}>{language == 'bangla' ? "নির্বাচন করুন" : "Select"}</option>
                    {instructions?.map((data, index) => {
                        return <option key={index} value={language == 'bangla' ? data?.value_bn : data?.value_en}>{language == 'bangla' ? data?.value_bn : data?.value_en}</option>
                    })}
                </select>
            </div>

            <div className="col-12 m-0 p-0 individual-input d-flex">
                <label htmlFor="exampleInputEmail1" className="form-label mt-1">
                    SIG:
                </label>
                <textarea className="mx-2 my-1 form-control input" autoComplete="off" type="text"
                    value={sig || ""}
                    // required={true} // optional
                    placeholder="SIG"
                    onChange={(e) => {
                        onSigChange(e.target.value);
                    }}
                    rows={3}
                />

            </div>

            <div className="col-12 m-0 p-0 individual-input d-flex">
                <label htmlFor="exampleInputEmail1" className="form-label mt-1">
                    Duration:
                </label>
                <AutoComplete
                    className="w-50 duration-autocomplete"
                    options={durationQuickPicks.map((p) => ({ ...p, label: p.value }))}
                    value={info?.duration?.split(" ")[0] || ""}
                    placeholder="Duration"
                    onChange={(text) => {
                        onFieldChange("duration", text);
                    }}
                    onSelect={(val, option) => {
                        // "2 days" -> 2 in the count field, Day(s) in the unit select
                        onDurationPick(option);
                    }}
                    filterOption={(inputValue, option) =>
                        option?.value?.toLowerCase?.().includes(inputValue?.toLowerCase?.() || "")
                    }
                />
                <select
                    value={info.duration_time}
                    onChange={(e) => {
                        onFieldChange("duration_time", e.target.value);
                    }}>
                    <option value={""}>{language == 'bangla' ? "নির্বাচন করুন" : "Select"}</option>
                    {durationTileData?.map((data, index) => {
                        return <option key={index} value={language == 'bangla' ? data?.value_bn : data?.value_en}>{language == 'bangla' ? data?.value_bn : data?.value_en}</option>
                    })}
                </select>

            </div>
        </div>
    )
}

export default SigFields
